import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import './HourlyDashboard.css';

// Number of hourly candles to fetch (720 = 30 days)
const CANDLE_LIMIT = 720;

function HourlyDashboard() {
  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const seriesRef         = useRef(null);

  // Bollinger Bands series refs
  const bbUpperRef = useRef(null);
  const bbMidRef   = useRef(null);
  const bbLowerRef = useRef(null);

  const [pair, setPair]                     = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [showBB, setShowBB]                 = useState(false);
  const [bbTimeframe, setBbTimeframe]       = useState('daily');
  const [bbLoading, setBbLoading]           = useState(false);

  // ── Create chart once on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#FFFBF3' },
        textColor: '#16244A',
      },
      grid: {
        vertLines: { color: '#EDE3CF' },
        horzLines: { color: '#EDE3CF' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: {
        borderColor: '#E3D9C6',
        timeVisible: true,
        secondsVisible: false,
      },
      width:  chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C',
      downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C',
      wickDownColor: '#C0571F',
    });

    chartRef.current  = chart;
    seriesRef.current = candleSeries;

    // Resize observer
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    ro.observe(chartContainerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
      bbUpperRef.current = null;
      bbMidRef.current   = null;
      bbLowerRef.current = null;
    };
  }, []);

  // ── Fetch available pairs on mount ─────────────────────────────────────────
  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  // ── Fetch hourly OHLC data whenever pair changes ───────────────────────────
  useEffect(() => {
    if (!pair || !seriesRef.current) return;

    setLoading(true);
    setError(null);

    fetch(`/api/ohlc?pair=${encodeURIComponent(pair)}&limit=${CANDLE_LIMIT}`)
      .then(r => r.json())
      .then(json => {
        // Convert ISO timestamps → Unix seconds (required for intraday bars)
        const raw = (json.data || []).map(row => ({
          time:  Math.floor(new Date(row.time).getTime() / 1000),
          open:  parseFloat(row.open),
          high:  parseFloat(row.high),
          low:   parseFloat(row.low),
          close: parseFloat(row.close),
        }));

        // Deduplicate by timestamp, then sort ascending
        const seen = new Map();
        raw.forEach(c => seen.set(c.time, c));
        const candles = Array.from(seen.values()).sort((a, b) => a.time - b.time);

        seriesRef.current.setData(candles);
        chartRef.current.timeScale().fitContent();
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load chart data.');
        setLoading(false);
      });
  }, [pair]);

  // ── Helper: clear BB series from chart ────────────────────────────────────
  const clearBbSeries = () => {
    if (!chartRef.current) return;
    if (bbUpperRef.current) {
      try { chartRef.current.removeSeries(bbUpperRef.current); } catch (_) {}
      bbUpperRef.current = null;
    }
    if (bbMidRef.current) {
      try { chartRef.current.removeSeries(bbMidRef.current); } catch (_) {}
      bbMidRef.current = null;
    }
    if (bbLowerRef.current) {
      try { chartRef.current.removeSeries(bbLowerRef.current); } catch (_) {}
      bbLowerRef.current = null;
    }
  };

  // ── Add / remove Bollinger Bands series when showBB, pair, or timeframe changes
  useEffect(() => {
    if (!chartRef.current) return;

    if (!showBB) {
      clearBbSeries();
      return;
    }

    if (!pair) return;

    clearBbSeries();

    bbUpperRef.current = chartRef.current.addSeries(LineSeries, {
      color: '#A16D17',
      lineWidth:        1,
      lineStyle:        2,
      priceLineVisible: false,
      lastValueVisible: false,
      title:            'BB Upper',
    });
    bbMidRef.current = chartRef.current.addSeries(LineSeries, {
      color: '#3A17A1',
      lineWidth:        1,
      lineStyle:        0,
      priceLineVisible: false,
      lastValueVisible: false,
      title:            'BB Mid',
    });
    bbLowerRef.current = chartRef.current.addSeries(LineSeries, {
      color: '#A16D17',
      lineWidth:        1,
      lineStyle:        2,
      priceLineVisible: false,
      lastValueVisible: false,
      title:            'BB Lower',
    });

    setBbLoading(true);
    fetch(
      `/api/indicators/bollinger-bands?pair=${encodeURIComponent(pair)}&timeframe=${bbTimeframe}&days=30`
    )
      .then(r => r.json())
      .then(json => {
        const rows = json.data || [];

        const upper = [];
        const mid   = [];
        const lower = [];

        rows.forEach(row => {
          const t = Math.floor(new Date(row.time).getTime() / 1000);
          if (row.bb_upper != null) upper.push({ time: t, value: parseFloat(row.bb_upper) });
          if (row.bb_mid   != null) mid.push(  { time: t, value: parseFloat(row.bb_mid)   });
          if (row.bb_lower != null) lower.push( { time: t, value: parseFloat(row.bb_lower) });
        });

        const dedup = arr => {
          const m = new Map();
          arr.forEach(p => m.set(p.time, p));
          return Array.from(m.values()).sort((a, b) => a.time - b.time);
        };

        if (bbUpperRef.current) bbUpperRef.current.setData(dedup(upper));
        if (bbMidRef.current)   bbMidRef.current.setData(dedup(mid));
        if (bbLowerRef.current) bbLowerRef.current.setData(dedup(lower));

        setBbLoading(false);
      })
      .catch(() => {
        setBbLoading(false);
      });
  }, [showBB, pair, bbTimeframe]);

  return (
    <div className="hourly-dashboard">
      <div className="hourly-controls">
        <label htmlFor="hourly-pair-select" className="pair-label">Pair</label>
        <select
          id="hourly-pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span className="interval-badge">1H</span>
        <span className="candle-count-badge">Last {CANDLE_LIMIT} candles</span>

        <label className="bb-toggle">
          <input
            type="checkbox"
            checked={showBB}
            onChange={e => setShowBB(e.target.checked)}
          />
          <span className="bb-toggle-label">
            Bollinger Bands
            {bbLoading && <span className="bb-loading-dot" />}
          </span>
        </label>

        {showBB && (
          <div className="bb-timeframe-group">
            {['daily', '1h', '4h'].map(tf => (
              <button
                key={tf}
                type="button"
                className={`bb-tf-btn${bbTimeframe === tf ? ' active' : ''}`}
                onClick={() => setBbTimeframe(tf)}
              >
                {tf === 'daily' ? 'D' : tf.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {showBB && (
          <div className="bb-legend">
            <span className="bb-legend-item bb-upper">▬ Upper</span>
            <span className="bb-legend-item bb-mid">▬ Mid</span>
            <span className="bb-legend-item bb-lower">▬ Lower</span>
          </div>
        )}
      </div>

      <div className="hourly-charts-area">
        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading chart…</span>
          </div>
        )}
        {error && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}

        <div className="hourly-price-wrapper">
          <div ref={chartContainerRef} className="chart-container" />
        </div>
      </div>
    </div>
  );
}

export default HourlyDashboard;
